import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { StateTree } from '@makemydeal/dr-dash-types';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import {
    EXPLICIT_LENDER_CARRIED_OVERRIDE_FIELDS,
    MILEAGE_COST_OPTIONS_OVERRIDE_FIELDS,
    setManualFieldOverride
} from '@makemydeal/dr-dash-store/actions/manualFieldOverridesActionCreators';
import * as manualFieldOverridesSelectors from '@makemydeal/dr-dash-store/selectors/manualFieldOverridesSelectors';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as offerSelectors from '@makemydeal/dr-dash-store/selectors/offerSelectors';
import type { RESIDUAL_TYPE } from '../constants';

// A union, not a string, so a typo at a call site fails to compile instead of returning `show: false`.
export type FieldOverrideKey =
    | 'buyRate'
    | 'sellRate'
    | 'mileageCharge'
    | 'acqFee'
    | 'maxResidualizedMsrp'
    | 'securityDeposit'
    | (typeof MILEAGE_COST_OPTIONS_OVERRIDE_FIELDS)[number]
    | RESIDUAL_TYPE;

export type FieldOverrideIndicatorState = {
    // True only when the toggle is on AND the indicator covers this field. Fields out of scope — e.g.
    // adjustedResidual — are never enabled, so the component renders them untouched.
    enabled: boolean;
    show: boolean;
    message: string;
    recordManualOverride: () => void;
};

export const FIELD_OVERRIDE_MESSAGE = 'You changed this value. It may reset if you update another field while Auto Select is on.';

// The shared message describes Improved Auto Select's fields, which reset on the next re-price and rely on
// a checkbox Explicit Lender P1 removes from the screen.
export const EXPLICIT_LENDER_OVERRIDE_MESSAGE = 'You changed this value. It is kept until a different lender program is applied.';

const MILEAGE_INDICATOR_FIELDS: ReadonlySet<FieldOverrideKey> = new Set<FieldOverrideKey>(MILEAGE_COST_OPTIONS_OVERRIDE_FIELDS);

const FIELD_OVERRIDE_MESSAGES: Partial<Record<FieldOverrideKey, string>> = Object.fromEntries(
    [...MILEAGE_INDICATOR_FIELDS].map((field) => [field, EXPLICIT_LENDER_OVERRIDE_MESSAGE])
);

const messageFor = (fieldKey: FieldOverrideKey, explicitLenderOn: boolean): string =>
    explicitLenderOn ? EXPLICIT_LENDER_OVERRIDE_MESSAGE : FIELD_OVERRIDE_MESSAGES[fieldKey] ?? FIELD_OVERRIDE_MESSAGE;

export const useFieldOverrideMessage = (fieldKey: FieldOverrideKey): string =>
    messageFor(fieldKey, useSelector(featureToggleSelectors.isExplicitLenderP1Enabled));

// An explicit list, so a new FieldOverrideKey member has no indicator until it is added here.
const INDICATOR_FIELDS: ReadonlySet<FieldOverrideKey> = new Set<FieldOverrideKey>([
    'buyRate',
    'sellRate',
    'mileageCharge',
    'baseResidual',
    'acqFee',
    'maxResidualizedMsrp',
    ...MILEAGE_INDICATOR_FIELDS
]);

// Fields whose indicator ships with the explicit-lender work rather than with Improved Auto Select. Keyed
// per field because the two toggles roll out separately, and a field must not appear under the other one.
const EXPLICIT_LENDER_FIELDS: ReadonlySet<FieldOverrideKey> = new Set<FieldOverrideKey>(['acqFee']);

// MRM (DE771374) ships behind its own toggle rather than piggybacking on Improved Auto Select's — the
// field was in scope for that feature but never built, so its rollout needs to be controllable
// independently of that feature's own (possibly already-advanced) rollout state.
const MRM_FIELDS: ReadonlySet<FieldOverrideKey> = new Set<FieldOverrideKey>(['maxResidualizedMsrp']);

export const useFieldOverrideIndicator = (fieldKey: FieldOverrideKey): FieldOverrideIndicatorState => {
    const dispatch = useDispatch();
    // Resolved inside one selector rather than by reading every gating toggle, so a field is gated only by
    // its own toggle. Explicit Lender P1 is read separately because it also chooses the message.
    const toggleOn = useSelector((state: StateTree) => {
        if (EXPLICIT_LENDER_FIELDS.has(fieldKey)) return featureToggleSelectors.isExplicitLenderP1Enabled(state);
        if (MRM_FIELDS.has(fieldKey)) return featureToggleSelectors.isMrmOverrideIndicatorEnabled(state);
        if (MILEAGE_INDICATOR_FIELDS.has(fieldKey)) return offerReduxSelectors.isAspPaymentGridRedesignActive(state);
        return featureToggleSelectors.isImprovedAutoSelectEnabled(state);
    });
    const explicitLenderOn = useSelector(featureToggleSelectors.isExplicitLenderP1Enabled);
    const carriedUnderExplicitLender = explicitLenderOn && EXPLICIT_LENDER_CARRIED_OVERRIDE_FIELDS.includes(fieldKey);
    const isIndicatorField = INDICATOR_FIELDS.has(fieldKey);
    const overridden = useSelector((state: StateTree) =>
        manualFieldOverridesSelectors.getManualFieldOverrides(state).includes(fieldKey)
    );
    const offerType = useSelector(offerReduxSelectors.getCurrentOfferType);
    const isAutoSelectOn = useSelector((state: StateTree) =>
        manualFieldOverridesSelectors.getResolvedAutoSelectChecked(state, offerType)
    );

    // Improved Auto Select's fields only indicate while Auto Select is on. The Explicit Lender P1 fields are
    // exempt: that toggle removes the checkbox, so `getResolvedAutoSelectChecked` falls back to the endpoint,
    // and a deal whose lender has no program for the current terms prices through user-program-quotes. Gating
    // on it there would hide the acquisition fee's indicator in exactly the case US2105291 says the manual
    // value is retained.
    const autoSelectSatisfied =
        EXPLICIT_LENDER_FIELDS.has(fieldKey) ||
        MILEAGE_INDICATOR_FIELDS.has(fieldKey) ||
        carriedUnderExplicitLender ||
        isAutoSelectOn;

    // The record says the dealer overrode the fee at some point; it cannot say the number on screen is still
    // theirs. Nine places replace or reset `acqFeeOverride`, most keyed on request state rather than on the
    // action, so no set of action types in the reducer can keep up. The selector compares the fee against
    // the selected term's own, which any of those writers falsifies without anything being maintained.
    const valueStillDealerEntered = useSelector((state: StateTree) => {
        if (fieldKey === 'acqFee') return offerSelectors.isDealerAcqFeeInForce(state);
        if (MILEAGE_INDICATOR_FIELDS.has(fieldKey)) return offerReduxSelectors.getIsUserProgramQuotes(state);
        return true;
    });

    const recordManualOverride = useCallback(() => {
        if (!carriedUnderExplicitLender && (!toggleOn || !isIndicatorField || !autoSelectSatisfied)) return;
        dispatch(setManualFieldOverride(fieldKey));
    }, [dispatch, carriedUnderExplicitLender, toggleOn, isIndicatorField, autoSelectSatisfied, fieldKey]);

    return useMemo<FieldOverrideIndicatorState>(() => {
        const enabled = toggleOn && isIndicatorField;
        const show = enabled && overridden && autoSelectSatisfied && valueStillDealerEntered;
        const message = show ? messageFor(fieldKey, explicitLenderOn) : '';
        return { enabled, show, message, recordManualOverride };
    }, [
        toggleOn,
        isIndicatorField,
        overridden,
        autoSelectSatisfied,
        valueStillDealerEntered,
        explicitLenderOn,
        fieldKey,
        recordManualOverride
    ]);
};
